/**
 * Module responsible to implement and alternative to 'window.rudderanalytics.ready' using 'RSA_Ready' event
 *
 * Overview:
 * - Do not use default 'window.rudderanalytics.ready()' instad use this module
 * - This module replaces the default 'window.rudderanalytics.ready()' with additional logic to avoid duplicate calls
 * - Rudderstack Doc: https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/supported-api/#ready-api
 *
 * Issues:
 *  - 'window.rudderanalytics.ready' is executed every time OneTrust is ready or when the customers changes the consent option
 *  - The callback functions added on 'window.rudderanalytics.ready' are not cleaned up once the callback is triggered
 *  - If Rudderstack SDK is loaded faster that OneTrust, then the 'window.rudderanalytics.ready' is executed and once OneTrust is ready it is executed again
 *
 * Use Cases:
 * - rudderanalyticsReadyCallback() function should be used on Page Loads event
 * - rudderanalyticsReadyCallback() will consider the OneTrust SDK status to trigger the callback function
 * - rudderanalyticsReadyCallback() will consider if Rudderstack ready event 'RSA_Ready' was already triggered to trigger the callback function immediately
 *
 */

import * as DataDogLogs from '~/lib/datadog/datadog';

/**
 * List of callback names to be used to identify the callback function
 */
const CALLBACK_NAMES = {
  AMPLITUDE_INIT_SESSION_REPLAY: 'amplitude-init-session-replay',
  DYNAMIC_YIELD_CLIENT_SIDE_CAMPAIGN: 'dynamic-yield-client-side-campaign',
  PRODUCT_HERO_SEND_PRODUCT_VIEWED_EVENT: 'product-hero-send-product-viewed-event',
  RUDDERSTACK_PAGE_LOAD: 'rudderstack-page-load',
  RUDDERSTACK_QUERY_STRING_API: 'rudderstack-query-string-api',
  USE_DYNAMIC_YIELD_CAMPAIGNS: 'use-dynamic-yield-campaigns',
};

// Pool of callbacks to be triggered when the RSA_Ready event is triggered
let ON_READY_CALLBACKS_POOL = [];

/**
 * Hook on 'RSA_Ready' event to trigger the callback functions
 *
 * This should be called on page load to hook on 'RSA_Ready' event
 * 'RSA_Ready' event is triggered after the RudderStack SDK is loaded
 * 'RSA_Ready' event is triggered after the OneTrust is ready or user consent is changed
 */
const onReadyEventInit = () => {
  document.addEventListener('RSA_Ready', () => {
    DataDogLogs.info('[Allbirds pixel] - RudderStack Ready - RSA_Ready event triggered');

    // Set the flag to indicate that the ready event was triggered
    window.ALLBIRDS.Rudderstack.isReadyEventTriggered = true;

    // Trigger the callback functions in the pool
    ON_READY_CALLBACKS_POOL.forEach((callbackToTrigger) => {
      callbackToTrigger();
    });

    // Clean up the pool after callback are triggered
    ON_READY_CALLBACKS_POOL = [];
    DataDogLogs.info('[Allbirds pixel] - RudderStack Ready - Pool - cleaned up');
  });
};

/**
 * Replacement for 'window.rudderanalytics.ready' from RudderStack SDK
 *
 * This function  should be used on Page Loads event
 * This function use the 'RSA_Ready' event to trigger the callback functions
 * This function use a pool of callbacks to be triggered when the 'RSA_Ready' event is triggered
 * This function will clean up the pool of callbacks after the callback is triggered
 * This function avoid duplicate calls to the callback functions
 * This function will consider the OneTrust SDK status to trigger the callback function
 * This function will consider if Rudderstack ready event 'RSA_Ready' was already triggered to trigger the callback function immediately
 *
 * @param {Function} callback
 */
const rudderanalyticsReadyCallback = (callbackFn, callbackFnName = 'unknown') => {
  if (typeof callbackFn !== 'function') {
    DataDogLogs.error(
      '[Allbirds pixel] - RudderStack Ready - callback is not a function',
      {
        callbackFn,
        callbackFnName,
      },
      new Error('callbackFn is not a function'),
    );

    return;
  }

  /**
   * Wrap the callback function with error handling and OneTrust SDK check
   *
   * @param {Function} callbackFn - The callback function to be triggered
   */
  const callbackFnWithErrorHandlingAndConsentCheck = () => {
    const nameOfTheCallback = callbackFnName;
    const callbackFnToTrigger = callbackFn;

    try {
      DataDogLogs.info('[Allbirds pixel] - RudderStack Ready - callback triggered', {
        nameOfTheCallback,
      });

      // If OneTrust is ready, trigger the callback function
      if (window.ALLBIRDS.OneTrust.isOneTrustActiveGroupsReady()) {
        callbackFnToTrigger();
      } else {
        // If OneTrust is not ready, push analytics events into a pool to be triggered once OneTrust is ready
        window.ALLBIRDS.OneTrust.onConsentChange(() => {
          callbackFnToTrigger();
        });
      }
    } catch (error) {
      DataDogLogs.error('[Allbirds pixel] - RudderStack Ready - error while triggering callback', {
        error,
        nameOfTheCallback,
      });
    }
  };

  // If Rudderstack ready event 'RSA_Ready' was already emitted, trigger the callback function immediately
  // Otherwise, add the callback to the pool to be triggered when the 'RSA_Ready' event is triggered
  if (window.ALLBIRDS.Rudderstack.isReadyEventTriggered) {
    DataDogLogs.info('[Allbirds pixel] - RudderStack Ready - callback triggered immediately', {
      callbackFnName,
    });

    callbackFnWithErrorHandlingAndConsentCheck();
  } else {
    // Add the callback to the pool
    ON_READY_CALLBACKS_POOL.push(callbackFnWithErrorHandlingAndConsentCheck);
    DataDogLogs.info('[Allbirds pixel] - RudderStack Ready - Pool - new callback added', {
      callbackFnName,
      poolSize: ON_READY_CALLBACKS_POOL.length,
    });
  }
};

export { CALLBACK_NAMES, onReadyEventInit, rudderanalyticsReadyCallback };
